import { useNavigate } from "react-router-dom";
import { Box, Button, Typography, Link } from "@mui/material";
import SignupSplitLayout from "./SignupSplitLayout";

interface SignupEntryStepProps {
  onEmailSignup: () => void;
  onGoogleSignIn: () => void;
}

const SignupEntryStep = ({
  onEmailSignup,
  onGoogleSignIn,
}: SignupEntryStepProps) => {
  const navigate = useNavigate();

  return (
    <SignupSplitLayout>
      <Box
        sx={{
          width: "100%",
          maxWidth: "448px",
          px: { xs: 3, sm: 3 },
          py: { xs: 5, sm: 4 },
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: "grey.900",
            mb: 1,
            textAlign: "center",
            fontSize: { xs: "18px", sm: "22px" },
          }}
        >
          Turn webinars into revenue.
        </Typography>
        <Typography
          sx={{
            color: "grey.600",
            mb: 4,
            textAlign: "center",
            fontSize: "14px",
          }}
        >
          Launch in minutes with EasyWebinar AI.
        </Typography>

        <Button
          onClick={onGoogleSignIn}
          fullWidth
          variant="outlined"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 3,
            px: 3,
            py: 1.5,
            borderColor: "grey.300",
            borderRadius: "14px",
            color: "#000",
            textTransform: "none",
            mb: 2,
            "&:hover": {
              bgcolor: "grey.50",
              borderColor: "grey.300",
            },
          }}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g clipPath="url(#clip0_3316_14656)">
              <path
                d="M18.8 10.208C18.8 9.55801 18.7417 8.93301 18.6333 8.33301H10V11.883H14.9333C14.7167 13.0247 14.0667 13.9913 13.0917 14.6413V16.9497H16.0667C17.8 15.3497 18.8 12.9997 18.8 10.208Z"
                fill="#4285F4"
              />
              <path
                d="M9.99974 19.1667C12.4747 19.1667 14.5497 18.35 16.0664 16.95L13.0914 14.6417C12.2747 15.1917 11.2331 15.525 9.99974 15.525C7.61641 15.525 5.59141 13.9167 4.86641 11.75H1.81641V14.1167C3.32474 17.1083 6.41641 19.1667 9.99974 19.1667Z"
                fill="#34A853"
              />
              <path
                d="M4.86732 11.7416C4.68398 11.1916 4.57565 10.6083 4.57565 9.99993C4.57565 9.3916 4.68398 8.80827 4.86732 8.25827V5.8916H1.81732C1.19232 7.12493 0.833984 8.5166 0.833984 9.99993C0.833984 11.4833 1.19232 12.8749 1.81732 14.1083L4.19232 12.2583L4.86732 11.7416Z"
                fill="#FBBC05"
              />
              <path
                d="M9.99974 4.48301C11.3497 4.48301 12.5497 4.94967 13.5081 5.84967L16.1331 3.22467C14.5414 1.74134 12.4747 0.833008 9.99974 0.833008C6.41641 0.833008 3.32474 2.89134 1.81641 5.89134L4.86641 8.25801C5.59141 6.09134 7.61641 4.48301 9.99974 4.48301Z"
                fill="#EA4335"
              />
            </g>
            <defs>
              <clipPath id="clip0_3316_14656">
                <rect width="20" height="20" fill="white" />
              </clipPath>
            </defs>
          </svg>
          <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>
            Continue with Google
          </Typography>
        </Button>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            mb: 2,
          }}
        >
          <Box
            sx={{
              width: "16px",
              height: "16px",
              borderRadius: "4px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <svg
              width="14"
              height="15"
              viewBox="0 0 14 15"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M1.33496 8.66873C1.2088 8.66916 1.08511 8.63378 0.978258 8.56671C0.871406 8.49964 0.785778 8.40363 0.731322 8.28983C0.676866 8.17603 0.655818 8.04912 0.670622 7.92383C0.685426 7.79855 0.735476 7.68003 0.814956 7.58206L7.41496 0.782062C7.46446 0.724916 7.53193 0.686299 7.60628 0.67255C7.68062 0.658801 7.75744 0.670737 7.82411 0.706398C7.89078 0.742059 7.94335 0.799326 7.97318 0.8688C8.00301 0.938275 8.00834 1.01583 7.98829 1.08873L6.70829 5.10206C6.67055 5.20308 6.65787 5.31174 6.67135 5.41873C6.68483 5.52572 6.72406 5.62785 6.78568 5.71635C6.8473 5.80484 6.92947 5.87707 7.02514 5.92684C7.12081 5.9766 7.22712 6.00241 7.33496 6.00206H12.0016C12.1278 6.00163 12.2515 6.03701 12.3583 6.10408C12.4652 6.17115 12.5508 6.26716 12.6053 6.38096C12.6597 6.49476 12.6808 6.62167 12.666 6.74696C12.6512 6.87224 12.6011 6.99076 12.5216 7.08873L5.92162 13.8887C5.87211 13.9459 5.80465 13.9845 5.7303 13.9982C5.65595 14.012 5.57914 14.0001 5.51247 13.9644C5.4458 13.9287 5.39323 13.8715 5.3634 13.802C5.33357 13.7325 5.32824 13.655 5.34829 13.5821L6.62829 9.56873C6.66603 9.46771 6.67871 9.35905 6.66523 9.25206C6.65175 9.14507 6.61252 9.04294 6.5509 8.95444C6.48928 8.86595 6.40711 8.79372 6.31144 8.74396C6.21577 8.69419 6.10946 8.66838 6.00162 8.66873H1.33496Z"
                fill="#F5A623"
                stroke="#F5A623"
                strokeWidth="1.33333"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Box>
          <Typography sx={{ fontSize: "14px", color: "grey.600" }}>
            Takes less than 60 seconds
          </Typography>
        </Box>

        <Button
          onClick={onEmailSignup}
          fullWidth
          variant="contained"
          sx={{
            px: 3,
            py: 1.5,
            bgcolor: "#178EFC",
            borderRadius: "14px",
            fontWeight: 500,
            textTransform: "none",
            "&:hover": {
              bgcolor: "#006dd1",
            },
          }}
        >
          Continue with Email
        </Button>

        <Typography
          sx={{
            fontSize: "12px",
            color: "#9ca3af",
            textAlign: "center",
            lineHeight: 1.6,
            mt: 2,
          }}
        >
          By continuing, you agree to our{" "}
          <Link
            href="https://easywebinar.com/terms-conditions/"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              textDecoration: "underline",
              color: "#667EEA",
              whiteSpace: "nowrap",
              "&:hover": { color: "#667EEA" },
            }}
          >
            Terms of Service
          </Link>{" "}
          and{" "}
          <Link
            href="https://easywebinar.com/privacy/"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              textDecoration: "underline",
              color: "#667EEA",
              whiteSpace: "nowrap",
              "&:hover": { color: "#667EEA" },
            }}
          >
            Privacy Policy
          </Link>
        </Typography>

        <Typography sx={{ fontSize: "14px", textAlign: "center", display: "flex", justifyContent: "center", alignItems: "center", gap: 1, mt: 3 }}>
          <Typography component="span" sx={{ color: "#6B7280", fontSize: "14px" }}>
            Already have an account?{" "}
          </Typography>
          <Link
            component="button"
            onClick={() => navigate("/login")}
            sx={{
              fontWeight: 500,
              color: "#6066E9",
              textDecoration: "none",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            Log in
          </Link>
        </Typography>
      </Box>
    </SignupSplitLayout>
  );
};

export default SignupEntryStep;
